import { useCallback, useEffect, useState } from 'react'

import { notificationsApi } from '../api'
import type { PushPreferenceKey, PushPreferences } from '../types'

export type PushPreferencesPhase = 'loading' | 'ready' | 'error'

export const DEFAULT_PUSH_PREFERENCES: PushPreferences = {
  messages: true,
  friendships: true,
  participation: true,
  activity_updates: true,
  feedback: true,
  nearby_activities: true,
  other: true,
}

export interface PushPreferencesState {
  preferences: PushPreferences | null
  phase: PushPreferencesPhase
  error: unknown
  savingKey: PushPreferenceKey | null
  saveErrorKey: string | null
  retry: () => void
  setPreference: (key: PushPreferenceKey, value: boolean) => Promise<boolean>
}

/**
 * Server-owned push preferences. The six toggles only gate the push channel;
 * in-app rows are always stored (D-043). PATCH sends a partial merge and the
 * response is authoritative, so another device cannot leave the UI stale.
 */
export function usePushPreferences(): PushPreferencesState {
  const [preferences, setPreferences] = useState<PushPreferences | null>(null)
  const [phase, setPhase] = useState<PushPreferencesPhase>('loading')
  const [error, setError] = useState<unknown>(null)
  const [savingKey, setSavingKey] = useState<PushPreferenceKey | null>(null)
  const [saveErrorKey, setSaveErrorKey] = useState<string | null>(null)
  const [attempt, setAttempt] = useState(0)

  useEffect(() => {
    const controller = new AbortController()
    notificationsApi
      .getPushPreferences(controller.signal)
      .then((response) => {
        if (controller.signal.aborted) return
        setPreferences({
          ...DEFAULT_PUSH_PREFERENCES,
          ...response.preferences,
        })
        setPhase('ready')
        setError(null)
      })
      .catch((cause: unknown) => {
        if (controller.signal.aborted) return
        setError(cause)
        setPhase('error')
      })
    return () => controller.abort()
  }, [attempt])

  const retry = useCallback(() => {
    setError(null)
    setPhase('loading')
    setAttempt((value) => value + 1)
  }, [])

  const setPreference = useCallback(
    async (key: PushPreferenceKey, value: boolean): Promise<boolean> => {
      if (!preferences || savingKey) return false
      const previous = preferences
      setSaveErrorKey(null)
      setSavingKey(key)
      setPreferences({ ...previous, [key]: value })
      try {
        const response = await notificationsApi.updatePushPreferences({
          [key]: value,
        })
        setPreferences({
          ...DEFAULT_PUSH_PREFERENCES,
          ...response.preferences,
        })
        return true
      } catch {
        setPreferences(previous)
        setSaveErrorKey('notifications.push.preferencesSaveError')
        return false
      } finally {
        setSavingKey(null)
      }
    },
    [preferences, savingKey],
  )

  return {
    preferences,
    phase,
    error,
    savingKey,
    saveErrorKey,
    retry,
    setPreference,
  }
}
